import React from 'react'
import { CreditCard, Info, Shield, CheckCircle, BarChart3, Clock } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { ServiceConfigUtils } from '../../../config/services.utils'

interface PricingPreviewProps {
  className?: string
  compact?: boolean
}

export const PricingPreview: React.FC<PricingPreviewProps> = ({ 
  className,
  compact = false 
}) => {
  // Get service summary from configuration
  const serviceSummary = ServiceConfigUtils.getServiceSummary();
  if (compact) {
    // Simple, clean divider for compact mode
    return (
      <div 
        className={className}
        style={{
          borderTop: '1px solid #e5e7eb',
          margin: '24px 0 16px 0'
        }}
      />
    )
  }

  // Full version for sidebar with service-based information
  return (
    <div className={cn(
      "bg-white border border-gray-200 rounded-lg shadow-sm",
      className
    )}>
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <CreditCard className="w-4 h-4 text-blue-600" />
          Service Information
        </div>
      </div>
      
      <div className="p-4 space-y-3">
        {/* Service Options */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <BarChart3 className="w-4 h-4 text-green-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">Intelligence Services</div>
            <div className="text-xs text-gray-600 mt-1">
              Choose your service after completing setup
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {serviceSummary.serviceList}
            </div>
          </div>
        </div>

        {/* Service Count */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <CheckCircle className="w-4 h-4 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">Available Services</div>
            <div className="text-xs text-gray-600 mt-1">
              {serviceSummary.activeCount} professional services
            </div>
            {serviceSummary.comingSoonCount > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                +{serviceSummary.comingSoonCount} coming soon
              </div>
            )}
          </div>
        </div>

        {/* Delivery Timeframe */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <Clock className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">Delivery</div>
            <div className="text-xs text-gray-600 mt-1">
              Professional analysis and reports
            </div>
            <div className="text-xs text-gray-500 mt-1">
              2-4 weeks for one-time services
            </div>
          </div>
        </div>

        {/* Refund Policy */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <Shield className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">7-Day Guarantee</div>
            <div className="text-xs text-gray-600 mt-1">
              Full refund within 7 days
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-800 leading-relaxed">
              <strong>No payment required now.</strong> Complete your setup first, 
              then select the intelligence service that best fits your needs.
              {serviceSummary.hasFreeServices && (
                <span className="block mt-1">
                  ✨ Free trial options available!
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Service Features Highlight */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
          <div className="text-xs font-medium text-gray-900 mb-2">
            What You Get:
          </div>
          <div className="space-y-1 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span>Expert analysis and insights</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span>Actionable recommendations</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span>Dedicated analyst support</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span>Executive-ready deliverables</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingPreview